/**
 * ERProjects — Sprint 6 C3 (EFISSUE-820).
 *
 * Sidebar PROJECTS section. Sits above the existing FILTERS group.
 * Each project has a colored dot, fixed SDLC sub-filters with all-time
 * tag-match counts, and an expand/collapse chevron. `[+ new project]`
 * row at the bottom opens an inline name + color picker.
 *
 * Click behavior:
 *   - Project row → toggle expand/collapse only (no view change)
 *   - Activity sub-row → toggle activeFilter on the project name
 *     (same handler ERPinnedFilters uses)
 *   - Decisions sub-row → switch center view to Decisions and
 *     apply project filter
 *
 * Per-project collapse state lives in localStorage as
 * `relay_collapsed_project_<sanitized-name>`. Section-level
 * collapse is `relay_collapsed_projects`.
 */
import { useEffect, useMemo, useState } from "react";
import { slugify, type ProjectRecord } from "../lib/projectsMigration";
import type { SDLCFilter } from "../lib/sdlcFilters";
import { Caret } from "./Caret";

/** track:track-backlog--project-modal-rebuild-three-tab-layout-with-prd-versioning */
export interface PrdSummary {
  prd_version: number;
  prd_label: string;
  prd_last_tab: string;
}

type CenterView = "activity" | "decisions" | "profile" | "charter" | "team" | "how-relay-works" | "context" | "backlog" | "governance" | "project-mgmt" | "relay-admin" | "feature-testing" | "backup-my-git" | "project-background";

const SECTION_COLLAPSE_KEY = "relay_collapsed_projects";

interface Counts {
  activity: number;
  decisions: number;
}

interface Props {
  projects: ProjectRecord[];
  countsByProject: Record<string, Counts>;
  activeFilters: string[];
  onToggleFilter: (tag: string) => void;
  onOpenDecisions: (projectName: string) => void;
  /**
   * relay-S3-sdlcfilters — open the dedicated Backlog page, pre-filtered
   * to this project. Mirrors onOpenDecisions; the "backlog" SDLC row
   * routes here instead of onSelectSdlcFilter (see sdlcFilters.ts's
   * `backlog` target).
   */
  onOpenBacklog: (projectName: string) => void;
  /**
   * track:track-backlog--project-creation-ux-fix — opens the Project
   * Background modal in create mode. Replaces the old inline name+color
   * form; project creation now always goes through the modal so Vision
   * and the rest of the background context are captured up front.
   */
  onOpenCreateProject: () => void;
  /**
   * EFISSUE-838 — toggle the project+subFilter compound filter. Same handler
   * for click-to-apply and click-again-to-clear; the parent's compoundFilter
   * state owns the "currently active" pointer.
   */
  onToggleSubFilter: (projectName: string, subFilter: string) => void;
  /**
   * EFISSUE-838 — append a new sub-filter to the project's subFilters array
   * in Firestore. Resolves once the write completes.
   */
  onCreateSubFilter: (projectName: string, subFilter: string) => Promise<void>;
  /**
   * relay-S3-sdlcfilters — remove a user-added sub-filter from the project's
   * `subFilters` array. Only fires for user-added rows; fixed SDLC filters
   * never expose a delete affordance.
   */
  onDeleteSubFilter: (projectName: string, subFilter: string) => Promise<void>;
  /**
   * EFISSUE-838 — currently-active compound filter, used to highlight the
   * matching sub-filter row. Null when no compound filter is set.
   */
  activeCompoundFilter: { project: string; subFilter: string } | null;
  /**
   * relay-S3-sdlcfilters — fixed SDLC filter set rendered before any
   * user-added sub-filters in each project row. Single source of truth
   * lives in `lib/sdlcFilters.ts`.
   */
  sdlcFilters: readonly SDLCFilter[];
  /**
   * relay-S3-sdlcfilters — currently-active SDLC sub-row. Drives both the
   * highlight on the matching row and the OR-mode tag set passed into the
   * activity stream.
   */
  activeSdlcFilter: { project: string; key: string } | null;
  /**
   * relay-S3-sdlcfilters — click handler for any non-Activity, non-
   * Decisions, non-Background SDLC row. Decisions continues to route
   * through `onOpenDecisions` so existing centerView swap behavior is
   * preserved exactly.
   */
  onSelectSdlcFilter: (projectName: string, filter: SDLCFilter) => void;
  /** Sprint 48 — open the Project Background modal for this project. */
  onOpenBackground: (projectName: string) => void;
  /**
   * track:track-backlog--project-modal-rebuild-three-tab-layout-with-prd-versioning
   * — PRD version summary per project, keyed by slugify(project.name).
   * Absent entries render no badge (no PRD saved yet).
   */
  prdByProject: Record<string, PrdSummary>;
  /**
   * relay-S3-sdlcfilters — center-view discriminator. Used to highlight
   * the Decisions SDLC row when the user is viewing that project's panel.
   */
  centerView: CenterView;
  decisionsProjectFilter: string | null;
  backlogProjectFilter: string | null;
}

function readSectionCollapsed(): boolean {
  try {
    const stored = localStorage.getItem(SECTION_COLLAPSE_KEY);
    // Default to collapsed if never set, otherwise read stored value
    return stored === null ? true : stored === "1";
  } catch {
    return true; // Default collapsed
  }
}

function projectCollapseKey(name: string): string {
  const sanitized = name.toLowerCase().replace(/[^a-z0-9-]/g, "-");
  return `relay_collapsed_project_${sanitized}`;
}

function readProjectCollapsed(name: string): boolean {
  try {
    return localStorage.getItem(projectCollapseKey(name)) === "1";
  } catch {
    return false;
  }
}

export function ERProjects({
  projects,
  countsByProject,
  activeFilters,
  onToggleFilter,
  onOpenDecisions,
  onOpenBacklog,
  onOpenCreateProject,
  onToggleSubFilter,
  onCreateSubFilter,
  onDeleteSubFilter,
  activeCompoundFilter,
  sdlcFilters,
  activeSdlcFilter,
  onSelectSdlcFilter,
  onOpenBackground,
  centerView,
  decisionsProjectFilter,
  backlogProjectFilter,
  prdByProject,
}: Props) {
  const [sectionCollapsed, setSectionCollapsed] = useState<boolean>(readSectionCollapsed);
  const toggleSection = () => {
    setSectionCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SECTION_COLLAPSE_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  return (
    <div>
      <button
        className="sb-summary"
        onClick={toggleSection}
        aria-expanded={!sectionCollapsed}
        style={{ marginBottom: 4 }}
      >
        <Caret open={!sectionCollapsed} />
        Projects
        <span
          className="plus"
          onClick={(e) => { e.stopPropagation(); onOpenCreateProject(); }}
          title="Add project"
          aria-label="Add project"
          role="button"
        >
          +
        </span>
      </button>
      {!sectionCollapsed && (
        <div className="sb-collapsible-body" style={{ display: "flex", flexDirection: "column", gap: 1, marginBottom: 8 }}>
          {projects.length === 0 && (
            <div
              style={{
                fontSize: 11,
                fontStyle: "italic",
                color: "var(--text3)",
                padding: "2px 4px",
              }}
            >
              No projects yet.
            </div>
          )}
          {projects.map((p) => (
            <ProjectRow
              key={p.name}
              project={p}
              counts={countsByProject[p.name] ?? { activity: 0, decisions: 0 }}
              activeFilters={activeFilters}
              onToggleFilter={onToggleFilter}
              onOpenDecisions={onOpenDecisions}
              onOpenBacklog={onOpenBacklog}
              onToggleSubFilter={onToggleSubFilter}
              onCreateSubFilter={onCreateSubFilter}
              onDeleteSubFilter={onDeleteSubFilter}
              activeCompoundFilter={activeCompoundFilter}
              sdlcFilters={sdlcFilters}
              activeSdlcFilter={activeSdlcFilter}
              onSelectSdlcFilter={onSelectSdlcFilter}
              onOpenBackground={onOpenBackground}
              centerView={centerView}
              decisionsProjectFilter={decisionsProjectFilter}
              backlogProjectFilter={backlogProjectFilter}
              prd={prdByProject[slugify(p.name)]}
            />
          ))}
          <NewProjectRow onOpen={onOpenCreateProject} />
        </div>
      )}
    </div>
  );
}

function ProjectRow({
  project,
  counts,
  activeFilters,
  onToggleFilter,
  onOpenDecisions,
  onOpenBacklog,
  onToggleSubFilter,
  onCreateSubFilter,
  onDeleteSubFilter,
  activeCompoundFilter,
  sdlcFilters,
  activeSdlcFilter,
  onSelectSdlcFilter,
  onOpenBackground,
  centerView,
  decisionsProjectFilter,
  backlogProjectFilter,
  prd,
}: {
  project: ProjectRecord;
  counts: Counts;
  activeFilters: string[];
  onToggleFilter: (tag: string) => void;
  onOpenDecisions: (projectName: string) => void;
  onOpenBacklog: (projectName: string) => void;
  onToggleSubFilter: (projectName: string, subFilter: string) => void;
  onCreateSubFilter: (projectName: string, subFilter: string) => Promise<void>;
  onDeleteSubFilter: (projectName: string, subFilter: string) => Promise<void>;
  activeCompoundFilter: { project: string; subFilter: string } | null;
  sdlcFilters: readonly SDLCFilter[];
  activeSdlcFilter: { project: string; key: string } | null;
  onSelectSdlcFilter: (projectName: string, filter: SDLCFilter) => void;
  onOpenBackground: (projectName: string) => void;
  centerView: CenterView;
  decisionsProjectFilter: string | null;
  backlogProjectFilter: string | null;
  prd?: PrdSummary;
}) {
  const [collapsed, setCollapsed] = useState<boolean>(() => readProjectCollapsed(project.name));
  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(projectCollapseKey(project.name), next ? "1" : "0"); } catch {}
      return next;
    });
  };
  const isActivityActive = activeFilters.includes(project.name);

  return (
    <div>
      <button
        type="button"
        className="sb-row sb-project"
        onClick={toggle}
      >
        <Caret open={!collapsed} />
        <span className="dot" style={{ background: project.color }} />
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {project.name}
        </span>
        {/* track:track-backlog--project-modal-rebuild-three-tab-layout-with-prd-versioning —
            PRD version + deep link to the project modal, opened to whichever
            of Background/Requirements was last saved (prd_last_tab). */}
        {prd && (
          <span
            role="button"
            tabIndex={0}
            title={`Open ${prd.prd_label || `PRD v${prd.prd_version}`}`}
            onClick={(e) => { e.stopPropagation(); onOpenBackground(project.name); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onOpenBackground(project.name);
              }
            }}
            style={{
              flexShrink: 0,
              fontSize: 10,
              fontWeight: 600,
              color: "var(--text3)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            PRD v{prd.prd_version} ↗
          </span>
        )}
      </button>
      {!collapsed && (
        <div className="sb-subgroup">
          {/* relay-S3-sdlcfilters — fixed SDLC filter set rendered in
              spec order. Activity/Decisions/Backlog route through their
              own dedicated handlers so each keeps its own centerView +
              highlight state; the rest (currently just Requirements) set
              sdlcFilter and stay in the activity stream. */}
          {sdlcFilters.map((f) => {
            const active = isSdlcRowActive({
              filter: f,
              project: project.name,
              isActivityActive,
              activeSdlcFilter,
              centerView,
              decisionsProjectFilter,
              backlogProjectFilter,
            });
            const count = sdlcRowCount(f.key, counts);
            const handleClick = () => {
              if (f.target === "background") {
                onOpenBackground(project.name);
                return;
              }
              if (f.key === "activity") {
                onToggleFilter(project.name);
                return;
              }
              if (f.target === "decisions") {
                onOpenDecisions(project.name);
                return;
              }
              if (f.target === "backlog") {
                onOpenBacklog(project.name);
                return;
              }
              onSelectSdlcFilter(project.name, f);
            };
            return (
              <SubRow
                key={f.key}
                label={f.label}
                count={count}
                active={active}
                onClick={handleClick}
              />
            );
          })}
          {/* EFISSUE-838 — user-defined sub-filters. Render after the
              fixed SDLC set; the inline `+ add filter` row sits below.
              relay-S3-sdlcfilters — only these rows expose a delete X. */}
          {(project.subFilters ?? []).map((sub) => {
            const isActive =
              activeCompoundFilter?.project === project.name &&
              activeCompoundFilter?.subFilter === sub;
            return (
              <SubRow
                key={sub}
                label={sub}
                count={0}
                active={isActive}
                onClick={() => onToggleSubFilter(project.name, sub)}
                onDelete={() => onDeleteSubFilter(project.name, sub)}
              />
            );
          })}
          <NewSubFilterRow
            existingSubFilters={project.subFilters ?? []}
            onCreate={(sub) => onCreateSubFilter(project.name, sub)}
          />
        </div>
      )}
    </div>
  );
}

function SubRow({
  label,
  active,
  onClick,
  onDelete,
}: {
  label: string;
  /** Kept on the prop interface so callers can still pass it (analytics +
   * future re-introduction). Not destructured / not rendered after
   * relay-S3-nobadges (#1701/#1648). */
  count?: number;
  active: boolean;
  onClick: () => void;
  /**
   * relay-S3-sdlcfilters — when present, render an X affordance on hover
   * that removes this sub-filter (user-added rows only — fixed SDLC rows
   * pass undefined and stay non-deletable).
   */
  onDelete?: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className={`sb-subrow${active ? " active" : ""}`}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <span style={{ flex: 1 }}>{label}</span>
      {/* relay-S3-nobadges (#1701/#1648) — count badge intentionally not
          rendered on per-project SDLC sub-rows. SDLC filters are browse
          surfaces, not inboxes; the count was visually conflating "30
          posts in this filter" with "30 things needing attention." `count`
          stays on the SubRow props for analytics + future re-introduction
          if the team re-decides. Inbox + Action Required keep their
          badges (rendered by other components). */}
      {onDelete && hover && (
        <button
          type="button"
          aria-label={`Remove filter ${label}`}
          title={`Remove filter ${label}`}
          onClick={(e) => {
            e.stopPropagation();
            void onDelete();
          }}
          style={{
            marginLeft: 4,
            padding: "0 4px",
            border: 0,
            background: "transparent",
            color: "var(--teal-darker, #054750)",
            fontSize: 13,
            lineHeight: 1,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}

/** Active-row helper for the SDLC fixed filter set. */
function isSdlcRowActive(args: {
  filter: SDLCFilter;
  project: string;
  isActivityActive: boolean;
  activeSdlcFilter: { project: string; key: string } | null;
  centerView: CenterView;
  decisionsProjectFilter: string | null;
  backlogProjectFilter: string | null;
}): boolean {
  const {
    filter, project, isActivityActive, activeSdlcFilter,
    centerView, decisionsProjectFilter, backlogProjectFilter,
  } = args;
  if (filter.key === "activity") {
    return isActivityActive && !activeSdlcFilter;
  }
  if (filter.target === "decisions") {
    return centerView === "decisions" && decisionsProjectFilter === project;
  }
  if (filter.target === "backlog") {
    return centerView === "backlog" && backlogProjectFilter === slugify(project);
  }
  return (
    activeSdlcFilter?.project === project &&
    activeSdlcFilter?.key === filter.key
  );
}

/** Map the SDLC filter key to the existing per-project counts, when one
 *  is already computed by App.tsx. Requirements currently renders without
 *  a count badge — adding a tag-keyed count is a follow-up. */
function sdlcRowCount(key: string, counts: Counts): number {
  if (key === "activity") return counts.activity;
  if (key === "decisions") return counts.decisions;
  return 0;
}

/**
 * EFISSUE-838 — inline `[+ add filter]` row scoped to a project.
 * Mirrors NewProjectRow's expand-on-click → name input → Add/Cancel
 * pattern. Validation: trim, reject empty, reject duplicates within the
 * same project, 32-char max, lowercase + alphanumerics + `-` only
 * (matches existing tag conventions).
 */
function NewSubFilterRow({
  existingSubFilters,
  onCreate,
}: {
  existingSubFilters: string[];
  onCreate: (subFilter: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [warn, setWarn] = useState<string | null>(null);

  const trimmed = name.trim().toLowerCase();
  const existingLower = useMemo(
    () => new Set(existingSubFilters.map((s) => s.toLowerCase())),
    [existingSubFilters],
  );

  useEffect(() => {
    if (!open) {
      setName("");
      setWarn(null);
    }
  }, [open]);

  const submit = async () => {
    if (!trimmed) {
      setWarn("Name required.");
      return;
    }
    if (trimmed.length > 32) {
      setWarn("Max 32 characters.");
      return;
    }
    if (!/^[a-z0-9-]+$/.test(trimmed)) {
      setWarn("Only a-z, 0-9, and - allowed.");
      return;
    }
    if (existingLower.has(trimmed)) {
      setWarn(`"${trimmed}" already exists.`);
      return;
    }
    setSubmitting(true);
    setWarn(null);
    try {
      await onCreate(trimmed);
      setOpen(false);
    } catch (err) {
      setWarn(err instanceof Error ? err.message : "Add failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <div
        className="sb-subrow add"
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        + add filter
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "4px 4px",
        background: "var(--surface2)",
        borderRadius: 4,
        marginTop: 2,
      }}
    >
      <input
        type="text"
        value={name}
        onChange={(e) => { setName(e.target.value); setWarn(null); }}
        placeholder="filter tag"
        autoFocus
        maxLength={32}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); submit(); }
          if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
        }}
        style={{
          width: "100%",
          boxSizing: "border-box",
          fontSize: 12,
          padding: "4px 8px",
          height: 26,
          borderRadius: 4,
          border: "1px solid var(--border)",
          background: "var(--bg)",
          color: "var(--text)",
          fontFamily: "var(--font-mono)",
          outline: "none",
          marginBottom: 4,
        }}
      />
      <div style={{ display: "flex", gap: 4 }}>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !trimmed}
          style={{
            flex: 1,
            fontSize: 11,
            padding: "4px 8px",
            borderRadius: 4,
            border: "1px solid var(--border)",
            background: "var(--surface)",
            color: "var(--text)",
            cursor: submitting ? "wait" : "pointer",
          }}
        >
          {submitting ? "Adding…" : "Add"}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={submitting}
          style={{
            fontSize: 11,
            padding: "4px 8px",
            borderRadius: 4,
            border: "1px solid var(--border)",
            background: "transparent",
            color: "var(--text2)",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
      {warn && (
        <div
          role="alert"
          style={{
            marginTop: 4,
            fontSize: 10,
            color: "var(--tag-blocker)",
          }}
        >
          {warn}
        </div>
      )}
    </div>
  );
}

function NewProjectRow({ onOpen }: { onOpen: () => void }) {
  return (
    <div
      className="sb-subrow add"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      + new project
    </div>
  );
}
